// src/features/auth/signup/utils/signup.utils.ts

import axios from 'axios';

import type {
  ApiErrorPayload,
  DuplicateCheckResponse,
  DuplicateField,
  SignupValidationParams,
  StoreListItem,
  StoreListResponse,
} from '../types/signup.types';

/*
 * 아이디 조건
 *
 * 1. 영문 시작
 * 2. 영문 + 숫자만
 * 3. 숫자 최소 1개
 * 4. 4~20자
 */
export const USER_ID_PATTERN =
  /^(?=[A-Za-z])(?=[A-Za-z0-9]*\d)[A-Za-z][A-Za-z0-9]{3,19}$/;

export const PHONE_NUMBER_PATTERN =
  /^01[016789]\d{7,8}$/;

export const VERIFICATION_CODE_PATTERN =
  /^\d{6}$/;

/*
 * Axios / 일반 에러 메시지
 */
export const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as
      | ApiErrorPayload
      | undefined;

    return (
      payload?.message ||
      payload?.error ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};

/*
 * 아이디 정규화
 */
export const normalizeUserId = (
  value: string,
): string => {
  return value
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 20);
};

/*
 * 전화번호 숫자만
 */
export const normalizePhoneNumber = (
  value: string,
): string => {
  return value
    .replace(/\D/g, '')
    .slice(0, 11);
};

/*
 * 010-0000-0000 형태
 */
export const formatPhoneNumber = (
  value: string,
): string => {
  const numbers =
    normalizePhoneNumber(value);

  if (numbers.length <= 3) {
    return numbers;
  }

  if (numbers.length <= 7) {
    return `${numbers.slice(
      0,
      3,
    )}-${numbers.slice(3)}`;
  }

  return `${numbers.slice(
    0,
    3,
  )}-${numbers.slice(
    3,
    7,
  )}-${numbers.slice(7)}`;
};

/*
 * 180초 -> 3:00
 */
export const formatVerificationTime = (
  seconds: number,
): string => {
  const minutes = Math.floor(
    seconds / 60,
  );

  const remainingSeconds =
    seconds % 60;

  return `${minutes}:${String(
    remainingSeconds,
  ).padStart(2, '0')}`;
};

/*
 * 백엔드 중복확인 응답 형태 대응
 */
export const isDuplicatedResponse = (
  response: DuplicateCheckResponse,
): boolean => {
  if (
    typeof response.available ===
    'boolean'
  ) {
    return !response.available;
  }

  if (
    typeof response.duplicated ===
    'boolean'
  ) {
    return response.duplicated;
  }

  if (
    typeof response.duplicate ===
    'boolean'
  ) {
    return response.duplicate;
  }

  if (
    typeof response.exists ===
    'boolean'
  ) {
    return response.exists;
  }

  return false;
};

/*
 * /store API 응답 형태 대응
 */
export const extractStoreList = (
  response: StoreListResponse,
): StoreListItem[] => {
  if (Array.isArray(response.data)) {
    return response.data;
  }

  if (
    response.data &&
    !Array.isArray(response.data)
  ) {
    if (
      Array.isArray(
        response.data.content,
      )
    ) {
      return response.data.content;
    }

    if (
      Array.isArray(
        response.data.stores,
      )
    ) {
      return response.data.stores;
    }
  }

  if (
    Array.isArray(response.content)
  ) {
    return response.content;
  }

  if (
    Array.isArray(response.stores)
  ) {
    return response.stores;
  }

  return [];
};

/*
 * 중복확인 전 입력 검증
 */
export const validateDuplicateField = (
  field: DuplicateField,
  value: string,
): string | null => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return field === 'userId'
      ? '아이디를 입력해주세요.'
      : '닉네임을 입력해주세요.';
  }

  if (
    field === 'userId' &&
    !USER_ID_PATTERN.test(
      trimmedValue,
    )
  ) {
    return '아이디는 영문으로 시작하고, 영문과 숫자를 포함한 4~20자로 입력해주세요.';
  }

  if (
    field === 'userNickname' &&
    (trimmedValue.length < 2 ||
      trimmedValue.length > 12)
  ) {
    return '닉네임은 2~12자로 입력해주세요.';
  }

  return null;
};

/*
 * 회원가입 전체 검증
 */
export const validateSignupForm = ({
  formData,
  passwordConfirm,
  duplicateStatus,
  phoneVerified,
  verificationToken,
  storeList,
}: SignupValidationParams):
  string | null => {
  const userId =
    formData.userId.trim();

  const userName =
    formData.userName.trim();

  const userNickname =
    formData.userNickname.trim();

  const phoneNumber =
    normalizePhoneNumber(
      formData.phoneNumber,
    );

  if (!userId) {
    return '아이디를 입력해주세요.';
  }

  if (
    !USER_ID_PATTERN.test(userId)
  ) {
    return '아이디는 영문으로 시작하고, 영문과 숫자를 포함한 4~20자로 입력해주세요.';
  }

  if (
    duplicateStatus.userId !==
    'available'
  ) {
    return '아이디 중복확인을 완료해주세요.';
  }

  if (!formData.password) {
    return '비밀번호를 입력해주세요.';
  }

  if (
    formData.password.length < 8
  ) {
    return '비밀번호는 8자 이상으로 입력해주세요.';
  }

  if (
    !/[A-Za-z]/.test(
      formData.password,
    )
  ) {
    return '비밀번호에는 영문이 포함되어야 합니다.';
  }

  if (
    !/\d/.test(formData.password)
  ) {
    return '비밀번호에는 숫자가 포함되어야 합니다.';
  }

  if (!passwordConfirm) {
    return '비밀번호 확인을 입력해주세요.';
  }

  if (
    formData.password !==
    passwordConfirm
  ) {
    return '비밀번호가 일치하지 않습니다.';
  }

  if (!userName) {
    return '이름을 입력해주세요.';
  }

  if (userName.length < 2) {
    return '이름은 2자 이상으로 입력해주세요.';
  }

  if (!userNickname) {
    return '닉네임을 입력해주세요.';
  }

  if (
    userNickname.length < 2 ||
    userNickname.length > 12
  ) {
    return '닉네임은 2~12자로 입력해주세요.';
  }

  if (
    duplicateStatus.userNickname !==
    'available'
  ) {
    return '닉네임 중복확인을 완료해주세요.';
  }

  if (!phoneNumber) {
    return '전화번호를 입력해주세요.';
  }

  if (
    !PHONE_NUMBER_PATTERN.test(
      phoneNumber,
    )
  ) {
    return '올바른 휴대전화 번호를 입력해주세요.';
  }

  if (!phoneVerified) {
    return '휴대폰 인증을 완료해주세요.';
  }

  if (!verificationToken) {
    return '휴대폰 인증 정보가 없습니다. 다시 인증해주세요.';
  }

  if (
    !Number.isInteger(
      formData.storeId,
    ) ||
    formData.storeId <= 0
  ) {
    return '소속 매장을 선택해주세요.';
  }

  const storeExists =
    storeList.some(
      (store) =>
        store.storeId ===
        formData.storeId,
    );

  if (!storeExists) {
    return '올바른 소속 매장을 선택해주세요.';
  }

  return null;
};